import { useCallback, useContext } from "react";
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from "@adsk/alloy-react-notification";
import {
  issueSnapshotCaptureStart,
  issueSnapshotFailure,
} from "@adsk/acc-issues-core/state/redux/ducks/issueSnapshots";
import { useDispatch } from "@adsk/acc-issues-core/state/redux/hooks";
import { useCurrentProjectId } from "@adsk/alloy-provider";
import { Issue } from "@adsk/acc-issues-types";
import { useTranslations } from "@adsk/acc-issues-translations";
import { CaptureSnapshotContext } from "@adsk/acc-issues-core/providers/CaptureSnapshotContext";

export const useCaptureSnapshot = () => {
  const captureSnapshot = useContext(CaptureSnapshotContext);
  const dispatch = useDispatch();
  const { showNotification } = useNotifications();
  const { T } = useTranslations();
  const projectId = useCurrentProjectId();

  return useCallback(
    async ({
      issue,
      flowId,
    }: {
      issue: Pick<Issue, "id">;
      flowId?: string;
    }): Promise<Blob | undefined> => {
      if (!projectId || !captureSnapshot) {
        return;
      }

      try {
        dispatch(
          issueSnapshotCaptureStart({
            issueId: issue.id,
            flowId,
          })
        );
        const snapshotBlob = await captureSnapshot(issue.id);

        if (!snapshotBlob) {
          throw new Error(
            `Failed to capture snapshot for issue ${issue.id} - got undefined blob`
          );
        }

        return snapshotBlob;
      } catch (e) {
        dispatch(issueSnapshotFailure({ issueId: issue.id }));
        showNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: T.translate("thumbnailField.failureMessage"),
        });
      }
    },
    [projectId, captureSnapshot, dispatch, showNotification, T]
  );
};
